/**
 * insightGen — rule-based, client-side insight generation.
 *
 * Phase 2: deterministic rules. Phase 3 will add LLM-backed alternatives
 * via Copilot. The functions here always return the same shape as
 * CruxInsight expects: { state, actions, reasons, confidence }.
 *
 * No network calls. No API keys. Safe to run on every render.
 */

const FALLBACK_ENERGY = {
  state:
    'Curx is waiting for your first energy log. Once you check in, the insight will personalise.',
  actions: [
    'Tap the Daily Check-In and rate today',
    'Be honest — even a low score helps',
    'Log a few days to see your trend',
  ],
  reasons: [
    'Insights need at least one log to ground in',
    'A consistent week unlocks the trend chart',
  ],
  confidence: 60,
};

const FALLBACK_GROWTH = {
  state:
    'Curx is waiting for your first skill entries. Add a few skills with a self-rated score to see your roadmap take shape.',
  actions: [
    'Add 3-5 skills you care about',
    'Rate each one honestly, 0-100',
    'Come back after one interview rep',
  ],
  reasons: [
    'No skill scores logged yet',
    'Insights ground themselves in your actual ratings',
  ],
  confidence: 55,
};

const FALLBACK_SYSTEM = {
  state:
    'Once Finance, Health, and Planner have a few entries, Curx will surface what matters most.',
  actions: [
    'Open the Money tab and log a transaction',
    'Log water or a meal in Health',
    'Add today’s tasks in Planner',
  ],
  reasons: [
    'Cross-module insights need data from at least one area',
    'A few days of activity unlocks the optimisation card',
  ],
  confidence: 55,
};

/* ================================================================
   ENERGY
   ================================================================ */

/**
 * generateEnergyInsight(last7Days)
 * @param {Array<{logged_at, mental_score, physical_score, stress_score}>} last7Days
 *        Most-recent-first preferred but we sort defensively.
 */
export function generateEnergyInsight(last7Days) {
  const rows = (last7Days || [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => new Date(b.logged_at) - new Date(a.logged_at));

  if (rows.length === 0) return FALLBACK_ENERGY;

  const today = rows[0];
  const mental = num(today.mental_score);
  const physical = num(today.physical_score);
  const stress = num(today.stress_score);

  const reasons = [];
  const actions = [];

  // Trend signals across last 3 days (if we have them)
  const trendDays = rows.slice(0, Math.min(3, rows.length));
  const stressTrendUp =
    trendDays.length >= 2 && allIncreasing(trendDays.map((r) => num(r.stress_score)));
  const mentalTrendDown =
    trendDays.length >= 2 && allDecreasing(trendDays.map((r) => num(r.mental_score)));
  const physicalTrendDown =
    trendDays.length >= 2 && allDecreasing(trendDays.map((r) => num(r.physical_score)));

  // Build state sentence
  let leadFragment;
  if (stress >= 7) leadFragment = 'You are running hot.';
  else if (mental <= 4 && physical <= 4) leadFragment = 'You are running on fumes.';
  else if (mental <= 4) leadFragment = 'Your mental energy is dipping.';
  else if (physical <= 4) leadFragment = 'Your body is asking for rest.';
  else if (mental >= 7 && physical >= 7 && stress <= 4) leadFragment = 'You are in a strong window.';
  else leadFragment = 'You are steady.';

  let detailFragment;
  if (stressTrendUp) detailFragment = 'Stress has been climbing for several days running.';
  else if (mentalTrendDown) detailFragment = 'Mental energy has been drifting down day over day.';
  else if (physicalTrendDown) detailFragment = 'Physical energy has been trending lower.';
  else detailFragment = `Today reads M${mental}/P${physical}/S${stress}.`;

  const state = `${leadFragment} ${detailFragment}`;

  // Build actions
  if (stress >= 7) actions.push('Take a 10-minute walk before lunch');
  if (physical <= 4) actions.push('Skip intense workouts — light movement only');
  if (mental <= 4) actions.push('Decline non-essential meetings today');
  if (mental >= 7 && physical >= 7 && stress <= 4)
    actions.push('Tackle your hardest work this morning');
  if (stress <= 3 && mental >= 6) actions.push('Block 90 minutes for deep focus');
  if (actions.length < 2) actions.push('Drink a glass of water before your next task');
  if (actions.length < 3 && stress >= 5) actions.push('Box breathing — 4 in, 4 hold, 4 out, 4 hold');
  // Trim to max 4
  while (actions.length > 4) actions.pop();

  // Build reasons
  if (stressTrendUp) reasons.push('Stress trending up 3 days');
  if (mentalTrendDown) reasons.push('Mental energy trending down 3 days');
  if (physicalTrendDown) reasons.push('Physical energy trending down 3 days');
  if (mental >= 7 && physical >= 7 && stress <= 4) reasons.push('All three metrics in healthy band');
  if (rows.length < 3) reasons.push(`Only ${rows.length} log${rows.length === 1 ? '' : 's'} so far this week`);
  if (reasons.length === 0) reasons.push("Today's scores are within your normal range");
  while (reasons.length > 3) reasons.pop();

  // Confidence — more data, more confidence; capped
  const confidence = Math.min(90, 55 + rows.length * 5);

  return { state, actions, reasons, confidence };
}

/* ================================================================
   SYSTEM
   ================================================================ */

/**
 * generateSystemInsight(finance, health, planner)
 * Inputs already normalised by useSystemData hook:
 *   finance: { incomeMTD, expensesMTD, balance, topSpend, deltaPct }
 *   health:  { waterMl, exerciseMin, mealsCount }
 *   planner: { dueToday, completedToday, upcomingThisWeek, overdue }
 */
export function generateSystemInsight(finance, health, planner) {
  const f = finance || {};
  const h = health || {};
  const p = planner || {};

  const noFinance = !f || (f.incomeMTD === 0 && f.expensesMTD === 0);
  const noHealth = !h || (h.waterMl === 0 && h.exerciseMin === 0 && h.mealsCount === 0);
  const noPlanner = !p || (p.dueToday === 0 && p.upcomingThisWeek === 0 && p.completedToday === 0);

  if (noFinance && noHealth && noPlanner) return FALLBACK_SYSTEM;

  const reasons = [];
  const actions = [];
  let lead = 'Most areas are on track.';

  // Finance signals
  if (f.expensesMTD > 0 && f.balance < 0) {
    lead = 'Spending is outpacing income this month.';
    reasons.push('Expenses higher than income MTD');
    actions.push('Review the largest spend categories in Money');
  } else if (typeof f.deltaPct === 'number' && f.deltaPct >= 20) {
    lead = `Spending is up ${f.deltaPct}% versus last month.`;
    reasons.push(`Spending +${f.deltaPct}% vs last month`);
    actions.push('Open Money and skim the last 7 days');
  } else if (f.topSpend && f.topSpend.amount > 0) {
    reasons.push(`Top category this month: ${f.topSpend.category}`);
  }

  // Planner signals
  if (p.overdue > 0) {
    lead = `${p.overdue} task${p.overdue === 1 ? '' : 's'} overdue and ${p.dueToday} due today.`;
    reasons.push(`${p.overdue} overdue + ${p.dueToday} due today`);
    actions.push('Knock out the smallest task first to unblock');
  } else if (p.dueToday >= 5) {
    lead = `${p.dueToday} tasks due today — that is a heavy day.`;
    reasons.push(`${p.dueToday} tasks due today`);
    actions.push('Defer two tasks to tomorrow to reduce load');
  } else if (p.dueToday > 0 && p.completedToday === 0) {
    actions.push(`Start the first of your ${p.dueToday} task${p.dueToday === 1 ? '' : 's'} due today`);
  }

  // Health signals
  if (h.waterMl > 0 && h.waterMl < 800) {
    reasons.push(`Only ${h.waterMl}ml water logged so far`);
    actions.push('Drink a glass of water before your next meeting');
  }
  if (h.exerciseMin === 0) {
    actions.push('A 10-minute walk counts — log it after');
  }

  // Encouragement if quiet day
  if (actions.length === 0) {
    actions.push('You are in a quiet window — invest 30 minutes in something you have been avoiding');
  }
  if (reasons.length === 0) {
    if (p.completedToday > 0) reasons.push(`${p.completedToday} task${p.completedToday === 1 ? '' : 's'} already done today`);
    else reasons.push('No urgent signals across modules');
  }

  // Cap lengths
  while (actions.length > 4) actions.pop();
  while (reasons.length > 3) reasons.pop();

  const filledModules = [!noFinance, !noHealth, !noPlanner].filter(Boolean).length;
  const confidence = Math.min(88, 50 + filledModules * 12);

  return { state: lead, actions, reasons, confidence };
}

/* ================================================================
   GROWTH
   ================================================================ */

/**
 * generateGrowthInsight({ currentSkills, allRows, lastInterviewAt })
 *
 *   currentSkills    — array of latest row per skill_name
 *                      ({ skill_name, category, score, recorded_at })
 *   allRows          — full raw rows from skill_scores (most-recent first)
 *   lastInterviewAt  — Date | null (most recent interview timestamp)
 */
export function generateGrowthInsight({
  currentSkills = [],
  allRows = [],
  lastInterviewAt = null,
} = {}) {
  if (!currentSkills.length) return FALLBACK_GROWTH;

  const scores = currentSkills.map((s) => num(s.score));
  const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const top = currentSkills.reduce((a, b) => (num(b.score) > num(a.score) ? b : a));
  const bottom = currentSkills.reduce((a, b) => (num(b.score) < num(a.score) ? b : a));

  // Has avg score moved? Compare current avg to oldest two-week-prior avg per skill.
  // We approximate: take each skill's earliest row inside the last 30 days,
  // average those, compare to current avg.
  const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const earlyPerSkill = {};
  for (const r of allRows.slice().reverse()) {
    const t = new Date(r.recorded_at).getTime();
    if (t < cutoff) continue;
    if (!earlyPerSkill[r.skill_name]) earlyPerSkill[r.skill_name] = num(r.score);
  }
  const earlyVals = Object.values(earlyPerSkill);
  const earlyAvg = earlyVals.length
    ? Math.round(earlyVals.reduce((a, b) => a + b, 0) / earlyVals.length)
    : null;
  const trendingUp = earlyAvg !== null && avg - earlyAvg >= 4;
  const trendingDown = earlyAvg !== null && earlyAvg - avg >= 4;

  // Interview recency
  const daysSinceInterview = lastInterviewAt
    ? Math.floor((Date.now() - new Date(lastInterviewAt).getTime()) / (24 * 60 * 60 * 1000))
    : null;
  const staleInterview = daysSinceInterview === null || daysSinceInterview >= 7;

  // State
  let lead;
  if (trendingUp) lead = 'Skill momentum is strong.';
  else if (trendingDown) lead = 'Skill momentum has slowed.';
  else if (avg >= 70) lead = 'Your skills are sitting in a strong band overall.';
  else if (avg < 40) lead = 'You are early in this skill stack — perfect time to ladder up.';
  else lead = 'Your skills are sitting in a working-on-it band.';

  let detail;
  if (num(bottom.score) < 50)
    detail = `${bottom.skill_name} is your weakest signal at ${num(bottom.score)}.`;
  else
    detail = `${top.skill_name} leads at ${num(top.score)} and the floor is at ${num(bottom.score)}.`;

  const state = `${lead} ${detail}`;

  // Actions
  const actions = [];
  if (staleInterview) {
    actions.push(
      daysSinceInterview === null
        ? 'Run your first 25-minute interview rep'
        : `${daysSinceInterview}+ days since your last interview rep — book one today`
    );
  }
  if (num(bottom.score) < 50) {
    actions.push(`Spend a focused 30 minutes on ${bottom.skill_name}`);
  }
  if (trendingDown) actions.push('Pick one skill and re-rate it after a deliberate practice block');
  if (trendingUp) actions.push('Stretch into the next level on the skill driving the momentum');
  if (actions.length < 2) actions.push('Pick the skill that matters most this quarter and dedicate today to it');
  while (actions.length > 4) actions.pop();

  // Reasons
  const reasons = [];
  if (trendingUp) reasons.push(`Average score up ${avg - earlyAvg} points this month`);
  if (trendingDown) reasons.push(`Average score down ${earlyAvg - avg} points this month`);
  if (num(bottom.score) < 50) reasons.push(`${bottom.skill_name} sits at ${num(bottom.score)}/100`);
  if (staleInterview && daysSinceInterview !== null)
    reasons.push(`Last interview rep was ${daysSinceInterview} day${daysSinceInterview === 1 ? '' : 's'} ago`);
  if (reasons.length === 0) reasons.push(`${currentSkills.length} skills tracked, average ${avg}`);
  while (reasons.length > 3) reasons.pop();

  // Confidence — more rows + recent interview = higher
  let confidence = 55 + Math.min(20, currentSkills.length * 2);
  if (lastInterviewAt && daysSinceInterview <= 7) confidence += 10;
  confidence = Math.min(90, confidence);

  return { state, actions, reasons, confidence };
}

/* ================================================================
   helpers
   ================================================================ */

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function allIncreasing(arr) {
  // arr is reverse-chronological; "increasing" means recent > older
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] <= arr[i + 1]) return false;
  }
  return true;
}

function allDecreasing(arr) {
  for (let i = 0; i < arr.length - 1; i++) {
    if (arr[i] >= arr[i + 1]) return false;
  }
  return true;
}
